import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { isDeltaEmpty } from '../../components/shared';
import { useTechnologyProductFamilies } from '../../graphql/queries';
import { deleteTechnologyProductFamily, ApiError } from '../../lib/apiClient';
import { usePermission } from '../../hooks/usePermission';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import type { TechnologyProductFamily } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/technology-families/')({
  component: TechnologyProductFamiliesPage,
});

export { TechnologyProductFamiliesPage };

function extractDescriptionText(value: string | null | undefined): string {
  if (!value || isDeltaEmpty(value)) return '';
  try {
    const delta = JSON.parse(value) as { ops?: Array<{ insert?: unknown }> };
    return (
      delta.ops
        ?.map((op) => (typeof op.insert === 'string' ? op.insert : ''))
        .join('')
        .trim() ?? ''
    );
  } catch {
    return value.trim();
  }
}

function TechnologyProductFamiliesPage(): JSX.Element {
  const navigate = useNavigate();
  const canManage = usePermission('technology:create');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useTechnologyProductFamilies();
  const families = [...(data?.technologyProductFamilies ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Deep-link target from the Technology detail page's Product Family field
  // (issue #1537, mirror of UJ-TECH-1257's family -> technologies link). A
  // bare `id` anchor isn't enough to locate one card among 30+ — scroll it
  // into view and apply a brief highlight so the destination is unambiguous.
  const [highlightedFamilyId, setHighlightedFamilyId] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    const targetId = window.location.hash.slice(1);
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    target.scrollIntoView({ block: 'center' });
    setHighlightedFamilyId(targetId);
    // 4s window — generous enough that a slow CI browser still has the class
    // in place when an E2E assertion checks it right after navigation.
    const timer = setTimeout(() => setHighlightedFamilyId(null), 4000);
    return () => clearTimeout(timer);
  }, [loading]);

  const CardElement = 'forge-card' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeIcon = 'forge-icon' as React.ElementType;

  const handleEdit = (family: TechnologyProductFamily): void => {
    void navigate({
      to: '/technology-families/$familyId/edit',
      params: { familyId: family.entityId },
    });
  };

  const handleDelete = async (
    e: React.MouseEvent,
    familyId: string,
    name: string
  ): Promise<void> => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteTechnologyProductFamily(familyId);
      setDeleteError(null);
      void refetch();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete family');
    }
  };

  if (loading) return <LoadingSpinner centered message="Loading product families..." />;

  if (error) {
    return (
      <EmptyState
        icon="error"
        title="Failed to load product families"
        description={error.message}
      />
    );
  }

  return (
    <div className="technology-families-page">
      <div className="page-header page-header--toolbar page-header--center page-header--spaced">
        <h1 className="forge-typography--headline4">Technology Product Families</h1>
        {canManage && (
          <Link to="/technology-families/new">
            <ForgeButton variant="raised">
              <ForgeIcon name="add" slot="start" />
              Create Family
            </ForgeButton>
          </Link>
        )}
      </div>

      {deleteError && (
        <p role="alert" className="technology-families-page__error">
          {deleteError}
        </p>
      )}

      {families.length === 0 ? (
        <EmptyState
          icon="folder"
          title="No product families"
          description="Create a product family to group technology products for Tech Radar navigation."
          actionLabel={canManage ? 'Create Family' : undefined}
          onAction={canManage ? () => void navigate({ to: '/technology-families/new' }) : undefined}
        />
      ) : (
        <div className="technology-families-page__grid">
          {families.map((family) => (
            <CardElement
              key={family.entityId}
              id={family.entityId}
              no-padding
              className={
                highlightedFamilyId === family.entityId
                  ? 'product-family-card product-family-card--highlighted'
                  : 'product-family-card'
              }
            >
              <div className="product-family-card__title-bar">
                <p className="product-family-card__title forge-typography--subtitle1">
                  {family.name}
                </p>
              </div>
              <div className="product-family-card__content">
                {extractDescriptionText(family.description) && (
                  <p className="product-family-card__description forge-typography--body2">
                    {extractDescriptionText(family.description)}
                  </p>
                )}
                <Link
                  to="/technologies"
                  search={{
                    page: 1,
                    pageSize: 25,
                    productFamilyId: family.entityId,
                    supplierId: '',
                  }}
                  className="product-family-card__count-link forge-typography--body2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ForgeIcon name="memory" className="product-family-card__count-icon" />
                  {family.technologyProductCount}{' '}
                  {family.technologyProductCount === 1
                    ? 'Technology Product'
                    : 'Technology Products'}
                </Link>
              </div>
              {canManage && (
                <div className="product-family-card__action-bar">
                  <ForgeButton
                    variant="outlined"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleEdit(family);
                    }}
                  >
                    Edit
                  </ForgeButton>
                  <ForgeButton
                    variant="outlined"
                    onClick={(e: React.MouseEvent) =>
                      void handleDelete(e, family.entityId, family.name)
                    }
                  >
                    Delete
                  </ForgeButton>
                </div>
              )}
            </CardElement>
          ))}
        </div>
      )}
    </div>
  );
}
